import React from 'react';
import styles from './styles.module.scss';
import Button from 'src/Components/Button_V2';
import icInfo from 'assets/svg/ic_info_outlined_V2.svg';
import Tooltip from 'src/Components/Tooltip';
import { TooltipPosition } from 'src/Components/Tooltip/types';
import classNames from 'classnames';
import icThetanGem from 'assets/img/thetan_gem_V2.png';
import { CurrencyType, formatCurrency, normalNumberToSystemCurrency } from '@wolffun/system-currency';
import ErrorView from './ErrorView';
import { checkErrorInvalidPrice } from '../../utils';
import { useTypedSelector } from 'src/redux/utils';
import { StakingPlan } from 'src/models';
import BN from 'bn.js';
import { AppError, EipError } from 'src/redux/types';
import { formatErrorMessage } from './utils';
import useCheckAllowance from 'src/hooks/useCheckAllowance';
import { STAKING_ADDRESS } from 'src/constants';

const FlexibleStakeContent = ({
  setAmountInput,
  doFlexibleStake,
  thgBalance
}) => {
  const stats = useTypedSelector(state => state.stakingStats.data);
  const {
    thgPlanStaked
  } = stats || {};
  const stakeable = thgPlanStaked && thgPlanStaked[StakingPlan.FLEXIBLE].stakeable || {
    token: CurrencyType.THG,
    value: '0'
  };
  const [displayPrice, setDisplayPrice] = React.useState('');
  const [error, setError] = React.useState(AppError.NO_ERROR);
  const isDisabled = !displayPrice || error !== AppError.NO_ERROR || Number(displayPrice) <= 0;
  const price = React.useMemo(() => {
    return normalNumberToSystemCurrency(Number(displayPrice), CurrencyType.THG);
  }, [displayPrice]);
  const {
    approveUseBalance,
    isEnoughAllowance,
    isProcessing
  } = useCheckAllowance({
    contractAddress: STAKING_ADDRESS,
    thresholdPrice: price
  });
  React.useEffect(() => {
    const price = normalNumberToSystemCurrency(Number(displayPrice), CurrencyType.THG);
    setError(checkErrorInvalidPrice(price, thgBalance, stakeable));
  }, [displayPrice]);

  const setMaxPrice = () => {
    const balanceBN = new BN(thgBalance.value);
    const stakeableBN = new BN(stakeable.value);
    const price = balanceBN.gt(stakeableBN) ? stakeable : thgBalance;
    const maxPrice = formatCurrency(price, {
      trimWholeFraction: true
    }).replace(',', '');
    setAmountInput(Number(maxPrice));
    setDisplayPrice(maxPrice);
  };

  const renderTooltip = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.tooltip
    }, /*#__PURE__*/React.createElement("span", null, "With the Flexible option, you can unstake at any time"));
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.row, styles.inputAmount)
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, /*#__PURE__*/React.createElement("span", null, "Amount"), /*#__PURE__*/React.createElement(Tooltip, {
    content: renderTooltip(),
    position: TooltipPosition.BOTTOM,
    color: "#3C32AF"
  }, /*#__PURE__*/React.createElement("img", {
    src: icInfo,
    className: styles.icInfo
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.fullWidth
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.priceRow
  }, /*#__PURE__*/React.createElement("input", {
    id: "amount-converted",
    placeholder: "1,234",
    className: classNames(styles.input, styles.price),
    type: "number",
    min: 0,
    value: displayPrice,
    maxLength: 20,
    onInput: e => {
      if (e.currentTarget.value.length > e.currentTarget.maxLength) e.currentTarget.value = e.currentTarget.value.slice(0, e.currentTarget.maxLength);
    },
    onChange: e => {
      setDisplayPrice(e.target.value);
      setAmountInput(Number(e.target.value));
    }
  }), /*#__PURE__*/React.createElement("button", {
    onClick: setMaxPrice,
    className: styles.maxBtn
  }, /*#__PURE__*/React.createElement("span", null, "Max")), /*#__PURE__*/React.createElement("div", {
    className: styles.unit
  }, /*#__PURE__*/React.createElement("img", {
    draggable: false,
    src: icThetanGem,
    className: styles.coin
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "THG")))), error !== AppError.NO_ERROR && /*#__PURE__*/React.createElement(ErrorView, {
    className: styles.error,
    title: formatErrorMessage(error)
  })), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.row, styles.amount)
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, /*#__PURE__*/React.createElement("span", null, "Your Balance:")), /*#__PURE__*/React.createElement("span", {
    className: styles.value
  }, /*#__PURE__*/React.createElement("span", null, thgBalance && formatCurrency(thgBalance, {
    withCurrency: true,
    trimWholeFraction: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.fullWidth
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, /*#__PURE__*/React.createElement("span", null, "Max to stake Flexible:")), /*#__PURE__*/React.createElement("span", {
    className: styles.value
  }, /*#__PURE__*/React.createElement("span", null, stakeable && formatCurrency(stakeable, {
    withCurrency: true,
    trimWholeFraction: true
  })))), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, isEnoughAllowance ? /*#__PURE__*/React.createElement(Button, {
    title: "Stake",
    type: "warning",
    className: styles.btnStake,
    onClick: doFlexibleStake,
    disabled: isDisabled
  }) : /*#__PURE__*/React.createElement(Button, {
    title: "Approve",
    type: "warning",
    className: styles.btnStake,
    onClick: async () => {
      const {
        error
      } = await approveUseBalance();

      if (error !== EipError.NO_ERROR) {
        setError(error);
      }
    },
    disabled: isDisabled || isProcessing
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.fullWidth
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.noticePlan
  }, /*#__PURE__*/React.createElement("span", null, "You can unstake at any time.")))));
};

export default FlexibleStakeContent;